import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BASE_URL } from 'utils/constant';

const AllocateInventoryModal = ({ 
  isOpen, 
  onClose, 
  taskId 
}) => {
  const [siteInventoryMaterials, setSiteInventoryMaterials] = useState([]);
  const [taskMaterials, setTaskMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      const siteId = localStorage.getItem("site");

      const [siteInventoryResponse, taskMaterialsResponse] = await Promise.all([
        fetch(`${BASE_URL}/inventory/get-materials-of-a-site/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
          },
          body: JSON.stringify({ sites: siteId })
        }),
        fetch(`${BASE_URL}/task/get-task-materials/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
          },
          body: JSON.stringify({ task_id: taskId })
        })
      ]);

      const siteInventoryData = await siteInventoryResponse.json();
      const taskMaterialsData = await taskMaterialsResponse.json();

      if (siteInventoryData.status && taskMaterialsData.status) {
        setSiteInventoryMaterials(siteInventoryData.data);
        setTaskMaterials(taskMaterialsData.data);
      } else {
        setError("Failed to fetch inventory data");
      }
    } catch (error) {
      setError("An error occurred while fetching inventory data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInventoryData();
    }
  }, [isOpen, taskId]);

  const isAlreadyInTaskInventory = (materialId) => {
    return taskMaterials.some(tm => tm.material_id === materialId);
  };

  const handleMaterialSelect = (material) => {
    const existingMaterialIndex = selectedMaterials.findIndex(
      m => m.material_id === material.id
    );

    if (existingMaterialIndex !== -1) {
      // Remove material if already selected
      const updatedMaterials = [...selectedMaterials];
      updatedMaterials.splice(existingMaterialIndex, 1);
      setSelectedMaterials(updatedMaterials);
    } else {
      // Add material with initial quantity 0
      setSelectedMaterials([
        ...selectedMaterials, 
        { 
          material_id: material.id, 
          material_name: material.material_name,
          quantity: 0,
          current_stock: material.current_stock 
        }
      ]);
    }
  };

  const handleQuantityChange = (materialId, newQuantity) => {
    const updatedMaterials = selectedMaterials.map(m => 
      m.material_id === materialId 
        ? { ...m, quantity: Math.min(parseFloat(newQuantity), m.current_stock) }
        : m
    );
    setSelectedMaterials(updatedMaterials);
  };

  const handleAllocate = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/task/create-task-materials/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          task_id: taskId,
          task_materials: selectedMaterials.map(m => ({
            material_id: m.material_id,
            quantity: m.quantity
          }))
        })
      });

      const data = await response.json();
      if (data.status) {
        // console.log("Materials allocated successfully");
        onClose();
        // You might want to trigger a refresh of materials in the parent component
      } else {
        setError(data.message || "Failed to allocate materials");
      }
    } catch (error) {
      setError("An error occurred while allocating materials");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-[600px] max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Allocate Inventory</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="p-4 text-center">Loading inventory...</div>
        ) : error ? (
          <div className="p-4 text-red-500 text-center">{error}</div>
        ) : (
          <div className="p-4 overflow-y-auto">
            {siteInventoryMaterials.map(material => (
              <div 
                key={material.id} 
                className={`flex items-center p-3 border-b ${
                  isAlreadyInTaskInventory(material.id) 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-gray-100 cursor-pointer'
                }`}
                onClick={() => 
                  !isAlreadyInTaskInventory(material.id) && handleMaterialSelect(material)
                }
              >
                <input 
                  type="checkbox" 
                  checked={selectedMaterials.some(m => m.material_id === material.id)}
                  disabled={isAlreadyInTaskInventory(material.id)}
                  readOnly
                  className="mr-4"
                />
                <div className="flex-1">
                  <div className="font-medium">{material.material_name}</div>
                  <div className="text-sm text-gray-500">
                    {material.specification} | {material.subcategory}
                  </div>
                </div>
                <div className="text-gray-600">
                  Current Stock: {material.current_stock} {material.uom}
                </div>

                {selectedMaterials.find(m => m.material_id === material.id) && (
                  <input 
                    type="number" 
                    min="0" 
                    max={material.current_stock}
                    value={
                      selectedMaterials.find(m => m.material_id === material.id)?.quantity || 0
                    }
                    onChange={(e) => handleQuantityChange(material.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="ml-4 w-24 p-1 border rounded"
                    placeholder="Qty"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="p-4 border-t flex justify-end space-x-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-600 border rounded"
          >
            Cancel
          </button>
          <button 
            onClick={handleAllocate}
            disabled={selectedMaterials.length === 0 || selectedMaterials.some(m => m.quantity === 0)}
            className="px-4 py-2 bg-[#3B0069] text-white rounded disabled:opacity-50"
          >
            Allocate Materials
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocateInventoryModal;